import { createSelector } from 'reselect';
import type { RootState } from '../../configuration/setup/store';
import { validWaypointPredicate, type Waypoint, WaypointType } from './searchSlice';

export const selectSearch = (state: RootState) => state.search;
export const selectWaypoints = createSelector(selectSearch, state => state.waypoints);
export const selectWaypointsOrder = createSelector(selectSearch, state => state.waypointsOrder);
export const selectIsAutomatedPlanningMode = createSelector(selectSearch, state => state.isAutomatedPlanningMode);
export const selectSuggestedAddresses = createSelector(selectSearch, state => state.suggestedAddresses);
export const selectSuggestedAddressesError = createSelector(selectSearch, state => state.fetchSuggestedAddressesError);
export const selectAreWaypointsInvalid = createSelector(selectWaypoints, (waypoints: Waypoint[]) => {
    const validWaypoints = waypoints.filter(validWaypointPredicate);
    const validStopovers = validWaypoints.filter(
        waypoint => waypoint.type === WaypointType.ADDITIONAL_STOP || waypoint.type === WaypointType.VEHICLE_LOCATION
    );
    return validStopovers.length < 2;
});
export const selectOrderedWaypoints = createSelector(
    selectWaypoints,
    selectWaypointsOrder,
    (waypoints: Waypoint[], order: number[]) =>
        order.map(waypointId => waypoints.find(point => point.id === waypointId)).filter(id => id !== undefined)
);
export const selectIsWaypointInRange = createSelector(
    [selectWaypoints, (_state: RootState, waypointId: number) => waypointId],
    (waypoints, waypointId) => {
        const waypointById = waypoints.find(waypoint => waypoint.id === waypointId);
        return waypointById?.isInRange;
    }
);
export const selectWaypointById = createSelector(
    [selectWaypoints, (_state: RootState, waypointId: number) => waypointId],
    (waypoints, waypointId) => {
        return waypoints.find(waypoint => waypoint.id === waypointId);
    }
);
export const selectWaypointMaxPowerById = createSelector(
    [selectWaypoints, (_state: RootState, waypointId: number) => waypointId],
    (waypoints, waypointId) => {
        const selectedWaypoint = waypoints.find(waypoint => waypoint.id === waypointId);
        return selectedWaypoint?.maxPower;
    }
);
export const selectHasChargingStationAsWp = createSelector(selectOrderedWaypoints, (waypoints: Waypoint[]) => {
    return waypoints.find(waypoint => waypoint.type === WaypointType.CHARGING_STATION);
});
export const selectWaypointCargoWeights = createSelector(selectWaypoints, (waypoints: Waypoint[]) => {
    return waypoints
        .filter(waypoint => waypoint.type === WaypointType.ADDITIONAL_STOP)
        .map(waypoint => waypoint.cargoAtDeparture ?? 0);
});
export const selectIsFirstWaypointVehicleLocation = createSelector(
    selectOrderedWaypoints,
    (waypoints: Waypoint[]) => waypoints.at(0)?.type === WaypointType.VEHICLE_LOCATION
);
